import React, { useState } from 'react';
import { MessageCircle, X, Send, Package, Award, Phone } from 'lucide-react';

const Chatbot = ({ chatbotOpen, setChatbotOpen, setCurrentPage, usine }) => {
  const [messages, setMessages] = useState([
    { sender: 'bot', text: `Bonjour ! Je suis l'assistant de ${usine.nom}. Comment puis-je vous aider aujourd'hui ?` }
  ]);
  const [step, setStep] = useState('menu');
  const [contact, setContact] = useState({ nom: '', email: '', tel: '', societe: '' });
  const [loading, setLoading] = useState(false);

  // Formspree configuration for commercial contact
  const FORMSPREE_ID = 'meepkevy';
  const FORMSPREE_URL = `https://formspree.io/f/${FORMSPREE_ID}`;

  const handleOption = (option) => {
    setMessages([...messages, { sender: 'user', text: option }]);
    
    setTimeout(() => {
      let response = '';
      let action = null;
      
      switch(option) {
        case 'produits':
          response = `${usine.nom} produit 3 produits principaux :\n• Acide Chlorhydrique (HCl)\n• Soude Caustique 50% (NaOH)\n• Hypochlorite de Sodium (NaClO)\n\nConsultez notre page Produits pour tous les détails techniques !`;
          action = () => setCurrentPage('produits');
          break;
        case 'nitrokym':
          response = `${usine.nom} est une usine spécialisée dans la chimie des chlor-alkalis, certifiée ISO 9001:2015. Fondée en ${usine.fondation}, nous employons ${usine.employes} personnes.`;
          action = () => setCurrentPage('nitrokym');
          break;
        case 'certification':
          response = `Nous sommes fiers d'être certifiés ISO 9001:2015. Cette certification garantit l'excellence de nos processus de production et notre engagement qualité total.`;
          action = () => setCurrentPage('certification');
          break;
        case 'devis':
          response = "Je vous redirige vers notre formulaire de devis. Vous recevrez une réponse personnalisée sous 24h.";
          action = () => setCurrentPage('devis');
          break;
        case 'contact':
          response = `Nos coordonnées :\n📞 ${usine.telephone}\n📧 ${usine.email}\n📍 ${usine.adresse}, ${usine.ville}\n\nOu utilisez notre formulaire de contact.`;
          action = () => setCurrentPage('contact');
          break;
        case 'commercial':
          setStep('form');
          response = "Parfait ! Remplissez ces informations pour qu'un commercial vous contacte :";
          break;
        default:
          response = "Comment puis-je vous aider ?";
      }
      
      setMessages(prev => [...prev, { sender: 'bot', text: response }]);
      
      if (action) {
        setTimeout(action, 1000);
      }
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submissionData = {
      societe: contact.societe,
      nom: contact.nom,
      email: contact.email,
      telephone: contact.tel,
      _subject: `Demande de contact commercial - ${contact.societe}`,
      _captcha: false
    };

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }

      setMessages([
        ...messages,
        { sender: 'user', text: `Informations envoyées pour ${contact.societe}` },
        { sender: 'bot', text: `Merci ${contact.nom} ! Un commercial de ${usine.nom} vous contactera bientôt à ${contact.email}.` }
      ]);
      setContact({ nom: '', email: '', tel: '', societe: '' });
      setStep('menu');
    } catch (error) {
      setMessages([
        ...messages,
        { sender: 'user', text: `Informations envoyées pour ${contact.societe}` },
        { sender: 'bot', text: `Désolé, une erreur s'est produite. Veuillez réessayer ou nous contacter directement.` }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Bouton Chatbot */}
      <button
        onClick={() => setChatbotOpen(!chatbotOpen)}
        className="chatbot-btn"
      >
        <MessageCircle size={28} />
      </button>

      {/* Fenêtre Chatbot */}
      {chatbotOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-title">
              <MessageCircle size={18} style={{ marginRight: '8px' }} />
              Assistant {usine.nom}
            </div>
            <button 
              onClick={() => setChatbotOpen(false)}
              className="chatbot-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.sender}`}>
                <div className="message-content">
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Menu */}
          {step === 'menu' && (
            <div className="chatbot-options">
              <button onClick={() => handleOption('produits')} className="option-btn">
                <Package size={14} style={{ marginRight: '6px' }} />
                Produits
              </button>
              <button onClick={() => handleOption('nitrokym')} className="option-btn">
                Usine
              </button>
              <button onClick={() => handleOption('certification')} className="option-btn">
                <Award size={14} style={{ marginRight: '6px' }} />
                Certification
              </button>
              <button onClick={() => handleOption('devis')} className="option-btn">
                Devis
              </button>
              <button onClick={() => handleOption('contact')} className="option-btn">
                <Phone size={14} style={{ marginRight: '6px' }} />
                Contact
              </button>
              <button onClick={() => handleOption('commercial')} className="option-btn contact">
                Contact commercial
              </button>
            </div>
          )}

          {/* Formulaire */}
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="chatbot-options" style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '15px' }}>
              <input
                type="text"
                placeholder="Société *"
                value={contact.societe}
                onChange={e => setContact({...contact, societe: e.target.value})}
                className="form-input"
                required
                style={{ fontSize: '14px', padding: '10px' }}
              />
              <input
                type="text"
                placeholder="Nom *"
                value={contact.nom}
                onChange={e => setContact({...contact, nom: e.target.value})}
                className="form-input"
                required
                style={{ fontSize: '14px', padding: '10px' }}
              />
              <input
                type="email"
                placeholder="Email *"
                value={contact.email}
                onChange={e => setContact({...contact, email: e.target.value})}
                className="form-input"
                required
                style={{ fontSize: '14px', padding: '10px' }}
              />
              <input
                type="tel"
                placeholder="Téléphone"
                value={contact.tel}
                onChange={e => setContact({...contact, tel: e.target.value})}
                className="form-input"
                style={{ fontSize: '14px', padding: '10px' }}
              />
              <button type="submit" className="option-btn contact" disabled={loading}>
                <Send size={16} style={{ marginRight: '8px' }} />
                {loading ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;